import { HeicConversionBackdrop } from '@/components/imageIntake/HeicConversionBackdrop';
import { useHeicConversion } from '@/components/imageIntake/useHeicConversion';
import { ImageEditorModal } from '@/components/modals/ImageEditorModal/ImageEditorModal';
import { useSubmitPartImageSearch } from '@/hooks/api/part-finder-image-search/useSubmitPartImageSearch';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useState } from 'react';
import { ImagePartFinderModal } from './ImagePartFinderModal';
import { PartImageIntake, PartImageIntakeDialog } from './PartImageIntakeDialog';

export interface FindFromImagesProperties {
  intakeOpen: boolean;
  onIntakeOpenChange: (open: boolean) => void;
}

export const FindFromImages = ({ intakeOpen, onIntakeOpenChange }: FindFromImagesProperties) => {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [submittedImages, setSubmittedImages] = useState<File[]>();
  const [context, setContext] = useState('');
  const [autoSplit, setAutoSplit] = useState(false);
  const { conversion, convertForBrowser } = useHeicConversion();
  const { isImageSearchCroppingDisabled } = useFeatureFlags();
  const submit = useSubmitPartImageSearch();

  const identify = (images: File[], partContext?: string, split = false) => {
    setPendingFiles([]);
    setSubmittedImages(images);
    submit.mutate({
      clientRequestId: crypto.randomUUID(),
      images,
      context: partContext?.trim() || undefined,
      autoSplit: split,
    });
  };

  const startWith = async ({ files, autoSplit: split, context: partContext }: PartImageIntake) => {
    onIntakeOpenChange(false);
    setSubmittedImages(undefined);
    submit.reset();
    setContext(partContext);
    setAutoSplit(split);
    const photos = await convertForBrowser(files);
    if (photos.length === 0) {
      return;
    }
    if (split || isImageSearchCroppingDisabled) {
      identify(photos, partContext, split);
    } else {
      setPendingFiles(photos);
    }
  };

  const close = () => {
    setSubmittedImages(undefined);
    submit.reset();
  };

  return (
    <>
      <PartImageIntakeDialog
        open={intakeOpen}
        onClose={() => onIntakeOpenChange(false)}
        onContinue={intake => void startWith(intake)}
      />
      <HeicConversionBackdrop conversion={conversion} />
      {pendingFiles.length > 0 && (
        <ImageEditorModal
          open
          files={pendingFiles}
          submitLabel='Identify'
          onComplete={editedFiles => identify(editedFiles, context, autoSplit)}
          onCancel={() => setPendingFiles([])}
        />
      )}
      {submittedImages && (
        <ImagePartFinderModal
          images={submittedImages}
          autoSplit={autoSplit}
          identificationId={submit.data?.id}
          isSubmitting={submit.isPending}
          submitError={submit.error ?? undefined}
          onClose={close}
        />
      )}
    </>
  );
};
